"use client";

import { useState, useEffect } from 'react';
import Link from "next/link";
import Image from 'next/image';
import { useTranslations } from 'next-intl';

export function Footer() {
  const [mounted, setMounted] = useState(false);
  const t = useTranslations('layout.footer');

  // Prevent hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 pt-12 pb-8">
      <div className="container px-4 md:px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-6">
              {/* Use SVG logos from public folder with theme switching */}
              {mounted && (
                <>
                  {/* Light theme logo */}
                  <Image
                    src="/logoBlack.svg"
                    alt="Cookiefy Logo"
                    width={120}
                    height={32}
                    className="h-8 w-auto block dark:hidden"
                    priority
                  />
                  {/* Dark theme logo */}
                  <Image
                    src="/logoWhite.svg"
                    alt="Cookiefy Logo"
                    width={120}
                    height={32}
                    className="h-8 w-auto hidden dark:block"
                    priority
                  />
                </>
              )}
              {/* Fallback while mounting */}
              {!mounted && (
                <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              )}
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
              {t('tagline')}
            </p>
            <div className="flex gap-4">
              <a href="#" className="text-gray-500 hover:text-blue-600 transition-colors" aria-label="Twitter">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a href="#" className="text-gray-500 hover:text-blue-600 transition-colors" aria-label="LinkedIn">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold mb-4 text-lg text-gray-900 dark:text-white">{t('product.title')}</h3>
            <ul className="space-y-3">
              <li><Link href="/features" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 transition-colors">{t('product.features')}</Link></li>
              <li><Link href="/pricing" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 transition-colors">{t('product.pricing')}</Link></li>
              <li><Link href="/customisation" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 transition-colors">{t('product.customization')}</Link></li>
              <li><Link href="/about-cookies" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 transition-colors">{t('product.aboutCookies')}</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold mb-4 text-lg text-gray-900 dark:text-white">{t('company.title')}</h3>
            <ul className="space-y-3">
              <li><Link href="/whyus" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 transition-colors">{t('company.about')}</Link></li>
               <li><Link href="/contact" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 transition-colors">{t('company.contact')}</Link></li>
              <li><Link href="/privacy-policy" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 transition-colors">{t('company.privacyPolicy')}</Link></li>
              <li><Link href="/terms-of-service" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 transition-colors">{t('company.terms')}</Link></li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-200 dark:border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('legal.copyright', { year: currentYear })}
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link href="/privacy-policy" className="text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 transition-colors">{t('legal.privacyPolicy')}</Link>
            <Link href="/terms-of-service" className="text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 transition-colors">{t('legal.terms')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}